<template>
  <div>
    <project-toolbar
      :selectMonth="true"
      :selectProject="true"
      :project="accountProject"
      :showCreditFilter="true"
    />

    <md-tabs
      class="cost-tag-tabs"
      v-if="selectedTab"
      :md-active-tab="selectedTab"
      @md-changed="tabChanged"
    >
      <template slot="md-tab" slot-scope="{ tab }">
        {{ tab.label }}
        <i class="badge" v-if="tab.data.tagCount > 0">{{
          tab.data.tagCount
        }}</i>
      </template>

      <md-tab
        v-for="tab in tabs"
        :key="tab.title"
        :id="tab.title"
        :md-label="tab.title"
        :md-template-data="{ tagCount: tab.tagCount }"
      />
    </md-tabs>

    <md-content>
      <div class="md-layout">
        <div
          class="md-layout-item md-size-66 md-small-size-100 md-xsmall-size-100 line-graph-card"
        >
          <md-card>
            <md-card-content>
              <GChart
                type="AreaChart"
                v-if="tabs.length > 0"
                :data="lineData"
                :options="lineOptions"
              />
            </md-card-content>
          </md-card>
        </div>

        <div
          class="md-layout-item md-size-33 md-small-size-100 md-xsmall-size-100"
        >
          <md-card>
            <md-card-content>
              <GChart
                v-if="tabs.length > 0"
                type="PieChart"
                :data="pieData"
                :options="pieOptions"
                ref="pieChart"
              />
            </md-card-content>
          </md-card>
        </div>
      </div>

      <div class="md-layout">
        <div
          class="md-layout-item md-size-100 md-small-size-100 md-xsmall-size-100"
        >
          <md-card>
            <md-card-content>
              <md-table v-model="tableData" md-thin md-stripe>
                <md-table-row slot="md-table-row" slot-scope="{ item }">
                  <md-table-cell :md-label="L('tag')" md-sort-by="name">{{
                    item.tag
                  }}</md-table-cell>
                  <md-table-cell :md-label="L('cost')" md-numeric>{{
                    item.costs.cost | currency(displayCurrency)
                  }}</md-table-cell>
                  <md-table-cell
                    v-if="!$config.feature.creditKind"
                    :md-label="L('credit')"
                    md-numeric
                    >{{
                      item.costs.credit | currency(displayCurrency)
                    }}</md-table-cell
                  >
                  <md-table-cell
                    v-if="$config.feature.creditKind"
                    :md-label="L('discount')"
                    md-numeric
                    >{{
                      item.costs.discount | currency(displayCurrency)
                    }}</md-table-cell
                  >
                  <md-table-cell
                    v-if="$config.feature.creditKind"
                    :md-label="L('promotion')"
                    md-numeric
                    >{{
                      item.costs.others | currency(displayCurrency)
                    }}</md-table-cell
                  >
                  <md-table-cell
                    v-if="$config.feature.creditKind"
                    :md-label="L('finalCost')"
                    md-numeric
                    >{{
                      item.costs.finalCost | currency(displayCurrency)
                    }}</md-table-cell
                  >
                  <md-table-cell v-else :md-label="L('finalCost')" md-numeric>{{
                    (item.costs.cost + item.costs.credit)
                      | currency(displayCurrency)
                  }}</md-table-cell>
                </md-table-row>
              </md-table>
            </md-card-content>
          </md-card>
        </div>
      </div>
    </md-content>
  </div>
</template>

<script>
import { GChart } from 'vue-google-charts'
import { mapState } from 'vuex'
import api from '@/api'
import { objectAdd, distinct, convertCosts } from '@/lib/utils'
import { getProject, getKindData } from '@/lib/aggutil'
import { iterateDateOverMonth } from '@/lib/dateutil'
import { getExchangeRate } from '@/lib/exchangeRate'
import ProjectToolbar from './ProjectToolbar'

export default {
  components: { ProjectToolbar, GChart },

  data() {
    return {
      tagsData: null,
      selectedTab: null,
      accountProject: {
        all: false,
        list: [],
      },
    }
  },

  computed: {
    ...mapState({
      curAccount: (state) => state.curAccount,
      curMonth: (state) => state.curMonth,
      curProject: (state) => state.curProject,
      exchangeRateList: (state) => state.exchangeRateList,
      creditFilter: (state) => state.creditFilter,
      displayCurrency: (state) => state.displayCurrency,
      userInfo: (state) => state.userInfo,
    }),

    projectId() {
      return this.curProject.id
    },

    lineData() {
      let lineData = null
      if (this.costData.length > 0) {
        lineData = [['date', ...this.costData.map((d) => d.tag)]]

        iterateDateOverMonth(this.curMonth, (date) => {
          lineData.push([
            date.format('DD'),
            ...this.costData
              .map((d) =>
                d.data.find((dd) => dd.date === date.format('YYYY-MM-DD'))
              )
              .map((d) => (d ? d.costs.finalCost : 0)),
          ])
        })
      }
      return lineData
    },

    lineOptions() {
      return {
        tooltip: { trigger: 'focus' },
        aggregationTarget: 'category',
        hAxis: { title: `${this.L('date')}` },
        vAxis: { title: `${this.L('cost')} (${this.displayCurrency})` },
        focusTarget: 'category',
        legend: { position: 'top' },
        isStacked: true,
        chartArea: {
          left: window.innerWidth > 800 ? 100 : 60,
          right: 10,
        },
        backgroundColor: 'none',
      }
    },

    pieData() {
      return [
        ['tag', 'cost'],
        ...this.costData.map((d) => [d.tag, d.costs.finalCost]),
      ]
    },

    pieOptions() {
      return {
        chartArea: {
          width: '100%',
          height: '100%',
          top: 7,
          bottom: 7,
        },
        backgroundColor: 'none',
      }
    },

    tableData() {
      return this.costData
    },

    tabs() {
      return (this.tagsData || []).map((t) => {
        let tagCount = 0
        if (this.curProject.id) {
          if (t.data.find((d) => d.project_id === this.curProject.id)) {
            tagCount =
              distinct(
                t.data.find((d) => d.project_id === this.curProject.id).data,
                'tag'
              ).length - 1
          }
        } else {
          tagCount =
            distinct(
              t.data.map((d) => d.data),
              'tag'
            ).length - 1
        }
        return {
          title: t.kind,
          tagCount,
        }
      })
    },

    costData() {
      const data = getKindData(this.tagsData, this.selectedTab, this.projectId)

      return distinct(data, 'tag')
        .map((tag) => {
          const dailyData = data
            .filter((d) => d.tag === tag && d.date.startsWith(this.curMonth))
            .map((d) => ({ date: d.date, costs: this.convertCosts(d.costs) }))

          const extraCosts = data
            .filter((d) => d.tag === tag && !d.date.startsWith(this.curMonth))
            .reduce((c, v) => objectAdd(c, this.convertCosts(v.costs)), {})

          const firstDateData = dailyData.find(
            (d) => d.date === `${this.curMonth}-01`
          )
          if (firstDateData) {
            firstDateData.costs = objectAdd(firstDateData.costs, extraCosts)
          } else {
            dailyData.push({ date: `${this.curMonth}-01`, costs: extraCosts })
          }

          return {
            tag: tag === '' ? 'N/A' : tag,
            data: dailyData,
            costs: dailyData.reduce((c, v) => objectAdd(c, v.costs), {}),
          }
        })
        .sort((a, b) => b.costs.finalCost - a.costs.finalCost)
    },
  },

  watch: {
    curAccount() {
      this.load()
    },

    curMonth() {
      this.load()
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      if (!this.curAccount.id) {
        return
      }
      await getExchangeRate(this.curMonth)
      this.tagsData = await api.aggregate.costAllocationTags(
        this.curAccount.id,
        this.curMonth
      )
      this.selectedTab = this.tabs.length > 0 ? this.tabs[0].title : ''
      this.accountProject = getProject(
        this.tagsData,
        this.userInfo,
        this.curAccount
      )
      if (!this.curProject.id) {
        const curProject = { id: null, name: '' }
        if (!this.accountProject.all && this.accountProject.list.length > 0) {
          curProject.id = this.accountProject.list[0].id
          curProject.name = this.accountProject.list[0].name
        } else if (this.accountProject.all) {
          curProject.id = '__total'
        }
        this.$store.commit('curProject', curProject)
      }
    },

    getExchangeRate(currency) {
      const er = this.exchangeRateList.find(
        (e) => e.month === this.curMonth && e.code === currency
      )
      return er ? er.send_rate : { finalCost: 0 }
    },

    convertCosts(costs) {
      return convertCosts(
        costs,
        this.displayCurrency,
        this.getExchangeRate.bind(this),
        null,
        this.creditFilter
      )
    },

    tabChanged(val) {
      this.selectedTab = val
    },
  },
}
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 800px) {
  .md-tabs {
    overflow-x: auto;
    &::v-deep .md-button {
      min-width: 105px;
    }
  }
}

.badge {
  width: 19px;
  height: 19px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0px;
  right: 3px;
  background: #448aff;
  border-radius: 100%;
  color: #fff;
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  letter-spacing: -0.05em;
  font-family: 'Roboto Mono', monospace;
}

.md-tabs {
  z-index: 1;
}

.md-content {
  margin: 8px;
}

.md-card {
  margin: 8px;
}

.md-table {
  margin: 0;
}

.md-toolbar ::v-deep .md-toolbar-section-end {
  flex: 4;
}
</style>
